import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchOrgDeferrals,
  fetchOrgScheduleDetail,
  fetchRecognitionRuns,
  fetchRollForward,
  fetchScheduleReconciliation,
  fetchUnpostedReleases,
  rerunRecognition,
} from "@/api/subscriptionSchedules";
import type { OrgDeferralsQuery } from "@/interfaces/subscriptionSchedules";

/** The year's 24100 roll-forward. */
export function useRollForward(fy: number) {
  return useQuery({
    queryKey: ["subscriptionRollForward", fy],
    queryFn: () => fetchRollForward(fy),
  });
}

/** The organization list. Keyed on the whole query; keeps the last page on screen while paging. */
export function useOrgDeferrals(query: OrgDeferralsQuery) {
  return useQuery({
    queryKey: ["subscriptionOrgDeferrals", query],
    queryFn: () => fetchOrgDeferrals(query),
    placeholderData: keepPreviousData,
  });
}

/** One organization's cycles and lines. */
export function useOrgScheduleDetail(organizationId: number, fy: number) {
  return useQuery({
    queryKey: ["subscriptionOrgSchedule", organizationId, fy],
    queryFn: () => fetchOrgScheduleDetail(organizationId, fy),
  });
}

/** The reconciliation strip and its differences page, as of a date (today when omitted). */
export function useScheduleReconciliation(asOf?: string) {
  return useQuery({
    queryKey: ["subscriptionSchedulesReconciliation", asOf ?? "today"],
    queryFn: () => fetchScheduleReconciliation(asOf),
  });
}

/** This month's recognition run and a page of the history. */
export function useRecognitionRuns(page = 1, limit = 20) {
  return useQuery({
    queryKey: ["subscriptionRecognitionRuns", page, limit],
    queryFn: () => fetchRecognitionRuns(page, limit),
    placeholderData: keepPreviousData,
  });
}

/** Releases up to a month that have not posted. Null `through` = don't ask. */
export function useUnpostedReleases(through: string | null) {
  return useQuery({
    queryKey: ["subscriptionUnposted", through],
    queryFn: () => fetchUnpostedReleases(through as string),
    enabled: through != null,
  });
}

/** Rerun recognition, then refresh everything it can change. */
export function useRerunRecognition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: rerunRecognition,
    onSettled: () => {
      for (const key of [
        "subscriptionRecognitionRuns",
        "subscriptionRollForward",
        "subscriptionOrgDeferrals",
        "subscriptionOrgSchedule",
        "subscriptionSchedulesReconciliation",
        "subscriptionUnposted",
        "accountingPeriods",
        "journalEntries",
      ]) {
        qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}
